import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCalendarAlt,
  FaCheck,
  FaDownload,
  FaMapPin,
} from "react-icons/fa";

import "./AdmissionsPreview.css";

const TABS = [
  { id: "proceso", label: "Proceso de admisión" },
  { id: "calendario", label: "Calendario" },
  { id: "matriculacion", label: "Matriculación" },
  { id: "faq", label: "Preguntas frecuentes" },
];

const FORMS = [
  {
    level: "Nivel Inicial",
    detail: "Sala de 3, 4 y 5 años",
    url: "https://drive.google.com/file/d/1wF6MoNhU9gVGlWxqNodpTpOqkHSjM-GE/view",
  },
  {
    level: "Nivel Primario",
    detail: "1° a 6° grado",
    url: "https://drive.google.com/file/d/1Lf5NNHxMwULLadCH260h-KIogq4Yv6Zt/view",
  },
  {
    level: "Nivel Secundario",
    detail: "1° a 6° año",
    url: "https://drive.google.com/file/d/1heNeX7YwVNVXZs83_PqfZQ1CglAPjpFS/view",
  },
];

const STEPS = [
  {
    title: "1. Conocer el colegio",
    text: "Participá de una jornada de puertas abiertas o solicitá una visita con turno previo en secretaría.",
  },
  {
    title: "2. Preinscripción",
    text: "Descargá la planilla del nivel correspondiente, completala y presentala en secretaría.",
  },
  {
    title: "3. Entrevista y observación",
    text: "La familia se reúne con el equipo directivo y el aspirante participa de una jornada de actividades.",
  },
  {
    title: "4. Respuesta y matrícula",
    text: "Una vez confirmada la vacante, se presenta la documentación y se completa la matrícula.",
  },
];

const FAQS = [
  {
    question: "¿Cuáles son los requisitos de ingreso?",
    answer: "Los requisitos varían según el nivel educativo. La institución informa la documentación correspondiente durante la entrevista.",
  },
  {
    question: "¿Se ofrecen becas?",
    answer: "Hay alternativas de ayuda y descuentos que se evalúan con administración según cada situación familiar.",
  },
  {
    question: "¿Cuándo comienzan las clases?",
    answer: "El ciclo lectivo comienza en marzo, con actividades de ambientación previas según el nivel.",
  },
];

function ProcessPanel() {
  return (
    <>
      <div className="tc-notice">
        <span className="tc-eyebrow">Importante</span>
        <p>Las preinscripciones 2027 están abiertas para:</p>
        <ul className="tc-level-list">
          {["Nivel Inicial", "Nivel Primario", "Nivel Secundario"].map((level) => (
            <li key={level}>
              <span className="tc-round-icon"><FaCheck /></span>
              {level}
            </li>
          ))}
        </ul>
      </div>

      <div className="tc-step-grid">
        {STEPS.map((step) => (
          <article className="tc-step-card" key={step.title}>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
          </article>
        ))}
      </div>
    </>
  );
}

function CalendarPanel() {
  const dates = [
    "Inscripciones abiertas desde mayo de 2026.",
    "Entrega de documentación hasta junio de 2026.",
    "Entrevistas durante mayo y junio de 2026.",
  ];

  return (
    <div className="tc-notice">
      <span className="tc-eyebrow">Fechas importantes</span>
      <ul className="tc-detail-list">
        {dates.map((date) => (
          <li key={date}>
            <span className="tc-round-icon"><FaCalendarAlt /></span>
            <span>{date}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EnrollmentPanel() {
  const items = [
    ["Pago de matrícula", "Se realiza en administración una vez confirmada la vacante."],
    ["Presentación de documentación", "Incluye la documentación requerida para el nivel correspondiente."],
    ["Confirmación de vacante", "Con la documentación completa, el colegio confirma el ingreso."],
  ];

  return (
    <div className="tc-notice">
      <span className="tc-eyebrow">Proceso de matriculación</span>
      <p>Una vez admitido el estudiante, la familia completa estas instancias:</p>
      <ul className="tc-detail-list">
        {items.map(([title, detail]) => (
          <li key={title}>
            <span className="tc-round-icon"><FaMapPin /></span>
            <span><strong>{title}</strong><small>{detail}</small></span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FaqPanel() {
  return (
    <div className="tc-notice">
      <span className="tc-eyebrow">Preguntas frecuentes</span>
      <div className="tc-faq-list">
        {FAQS.map((faq) => (
          <details key={faq.question}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

const PANELS = {
  proceso: ProcessPanel,
  calendario: CalendarPanel,
  matriculacion: EnrollmentPanel,
  faq: FaqPanel,
};

export default function AdmissionsPreview() {
  const [activeTab, setActiveTab] = useState("proceso");
  const ActivePanel = PANELS[activeTab];

  return (
    <>
      <main className="tomas-connect-admissions">
        <section className="tc-admissions-shell" aria-labelledby="tc-admissions-title">
        <motion.header
          className="tc-admissions-header"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="tc-pill">Admisiones</span>
          <h1 id="tc-admissions-title">Preinscripciones 2027</h1>
          <p>Conocé el proceso de admisión y descargá la planilla correspondiente al nivel educativo.</p>
        </motion.header>

        <div className="tc-tabs" role="tablist" aria-label="Información de admisiones">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={activeTab === tab.id ? "is-active" : ""}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="tc-panel"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            role="tabpanel"
          >
            <ActivePanel />
          </motion.div>
        </AnimatePresence>

        <section className="tc-downloads" aria-labelledby="tc-downloads-title">
          <span className="tc-eyebrow">Documentación</span>
          <h2 id="tc-downloads-title">Planillas de preinscripción</h2>
          <div className="tc-download-grid">
            {FORMS.map((form) => (
              <a key={form.level} href={form.url} target="_blank" rel="noopener noreferrer">
                <FaDownload aria-hidden="true" />
                <strong>{form.level}</strong>
                <span>{form.detail}</span>
                <small>Descargar planilla</small>
              </a>
            ))}
          </div>
        </section>
        </section>
      </main>
    </>
  );
}
